import { Button, DatePicker, Form, Input, message, Modal, Select, Spin, Tooltip, Typography } from "antd";
import { InfoCircleOutlined, LockOutlined, SyncOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";

import { controller } from "../controller";
import { controller as rootController } from "../../../controller";
import SikkaPolicyReviewModal from "../../InsuranceBreakDown/SikkaPolicyReviewModal";
import MemberDobSuggestion from "./shared/MemberDobSuggestion";
import PatientInfoCard, { sectionHeading } from "./shared/PatientInfoCard";
import { governmentPlanValues } from "../lib/governmentPlans";

import close from "../../../assets/icons/close-circle.svg";
import calendar from "../../../assets/icons/calendar.png";
import dayjs from "dayjs";
import { describeRepoint } from "../lib/carrierLabel";
import { stripUnmountedPlanFields } from "../lib/policyForm";



function renderFieldLabel(text, fieldName, lockedFields, infoTooltip) {
  const isLocked = Array.isArray(lockedFields) && lockedFields.indexOf(fieldName) !== -1;
  return (
    <span className="patient-policy-form-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {text}
      {isLocked ? (
        <Tooltip title="You edited this field. It will not be overwritten when fetching policies from PMS.">
          <LockOutlined style={{ color: "#6b43b5", fontSize: 12 }} aria-label="Locked from PMS sync" />
        </Tooltip>
      ) : null}
      {infoTooltip ? (
        <Tooltip title={infoTooltip}>
          <InfoCircleOutlined style={{ color: "#848696", cursor: "help" }} aria-label="Help" />
        </Tooltip>
      ) : null}
    </span>
  );
}




export default function PatientPolicyInfoFormModal({
  setShowPatientInsuranceFormModal,
  checkPatientInsuranceEligibility,
  showPatientInsuranceFormModal,
  setEditPatientInsuranceData,
  editPatientInsuranceData,
  setCancelProcess,
  handleReadData2,
  patientID,
  patient,
  initialMode,
  // Render the FORM ONLY — no <Modal>, no "Manage Policies" header, no policy-
  // list sidebar, no Patient Info card — so a host can lay it out inline. Mirrors
  // the `embedded` prop InsuranceBreakdownMasterModal / InsuranceEstimatesMasterModal
  // already take. Used by the Insurance Requests composer, whose Policy card expands
  // into this form in place rather than throwing a modal over the drawer.
  // Defaults false: every other caller is completely unaffected.
  embedded = false,
}) {
  const [form] = Form.useForm();
  // Watched member fields so the empty-DOB suggestion reacts live (incl. add-mode typing).
  const watchedFirst = Form.useWatch("first_name", form);
  const watchedLast = Form.useWatch("last_name", form);
  const watchedDob = Form.useWatch("date_of_birth", form);
  const [isSendingEligibilityData, setIsSendingEligibilityData] = useState(false);
  const [isGettingPatientInsuranceData, setIsGettingPatientInsuranceData] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [patientPolicies, setPatientPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(undefined);
  const [displayPatient, setDisplayPatient] = useState(null);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  const [isFetchingFromSikka, setIsFetchingFromSikka] = useState(false);
  const [sikkaPreview, setSikkaPreview] = useState(null);

  useEffect(function () {
    var cancelled = false;
    async function fetchCarriers() {
      try {
        setIsLoadingCarriers(true);
        var res = await rootController.listInsuranceProviders();
        if (!cancelled && Array.isArray(res.data)) {
          setCarrierOptions(res.data
            .filter(function (c) { return c.is_active; })
            .map(function (c) { return { value: c.code, label: c.name }; })
          );
        }
      } catch (err) {
        console.error("Failed to load carriers", err);
      } finally {
        if (!cancelled) setIsLoadingCarriers(false);
      }
    }
    fetchCarriers();
    return function () { cancelled = true; };
  }, []);

  // SECTION: Get data
  // useEffect(() => {
  //   async function getPatientInsurancePolicyInfo() {
  //     if (!editPatientInsuranceData) return;
  //     try {
  //       setIsGettingPatientInsuranceData(true);
  //       const response = await controller.checkPatientInsuranceInfo(patientID);

  //       if (response.status === 200) {
  //         form.setFieldsValue(response.json);
  //       }
  //     } catch (err) {
  //       console.error(err);
  //       message.error("Something went wrong while getting patient insurance data");
  //     } finally {
  //       setIsGettingPatientInsuranceData(false);
  //     }
  //   }
  //   getPatientInsurancePolicyInfo();
  // }, [editPatientInsuranceData, form, patientID]);

  useEffect(function () {
    getPatientPolicies();
  }, []);

  useEffect(
    function () {
      if (showPatientInsuranceFormModal) {
        if (initialMode === "add") {
          setShowForm(true);
          setSelectedPolicy(undefined);
          form.resetFields();
          return;
        }
        if (patientPolicies.length === 1) {
          setSelectedPolicy(patientPolicies[0]);
        }
      } else {
        setDisplayPatient(null);
      }
    },
    [showPatientInsuranceFormModal, initialMode],
  );

  useEffect(
    function () {
      if (selectedPolicy === undefined) return;

      form.setFieldsValue({
        member_id: selectedPolicy.member_id,
        insurance_type: selectedPolicy.insurance_type,
        contract_number: selectedPolicy.contract_number,
        insurance_name: selectedPolicy.insurance_name,
        first_name: selectedPolicy.first_name,
        last_name: selectedPolicy.last_name,
        date_of_birth: selectedPolicy.date_of_birth ? dayjs(selectedPolicy.date_of_birth, "YYYY-MM-DD") : undefined,
        relation_to_member: selectedPolicy.relation_to_member || undefined,
        plan_name: selectedPolicy.plan_name || undefined,
        fee_guide: selectedPolicy.fee_guide || undefined,
        specialist_fee_covered:
          selectedPolicy.specialist_fee_covered === true || selectedPolicy.specialist_fee_covered === false
            ? selectedPolicy.specialist_fee_covered
            : undefined,
        // Text now — Number() turned "$50 per person" into NaN and wiped it.
        deductible: selectedPolicy.deductible != null && selectedPolicy.deductible !== ""
          ? String(selectedPolicy.deductible) : undefined,
      });
    },
    [selectedPolicy],
  );

  // Fetch patient details for display when form is shown (use patient prop or fetch by ID)
  useEffect(
    function () {
      if (!showPatientInsuranceFormModal || !showForm || !patientID) {
        setDisplayPatient(null);
        return;
      }
      if (patient && patient.first_name) {
        setDisplayPatient(patient);
        return;
      }
      let cancelled = false;
      async function fetchPatient() {
        try {
          const response = await rootController.getPatientDetails(patientID);
          if (cancelled) return;
          if (response.status < 250 && response.json) {
            setDisplayPatient(response.json);
          }
        } catch {
          if (!cancelled) setDisplayPatient(null);
        }
      }
      fetchPatient();
      return () => {
        cancelled = true;
      };
    },
    [showPatientInsuranceFormModal, showForm, patientID, patient],
  );

  // SECTION: functions
  async function handleUpdateOrCreatePolicy() {
    try {
      const values = await form.validateFields(); // Get form values

      if (values.date_of_birth) {
        if (dayjs.isDayjs(values.date_of_birth)) {
          values.date_of_birth = values.date_of_birth.format("YYYY-MM-DD");
        }
      }

      // Plan fields that aren't on this form stay out of the request
      // (sending them as null erased them) — see lib/policyForm.
      const body = stripUnmountedPlanFields(values);

      setIsSendingEligibilityData(true);
      let created = false;

      let response;
      if (selectedPolicy && selectedPolicy.id) {
        // Update existing policy
        response = await rootController.updatePatientInsurancePolicy(selectedPolicy.id, body);
        created = true;
      } else {
        // Create new policy
        response = await controller.setPatientInsuranceInfo(patientID, body);
      }

      if (response.status < 250) {
        if (!editPatientInsuranceData) {
          checkPatientInsuranceEligibility();
          setCancelProcess(false);
        }

        if (created) {
          setSelectedPolicy(response.json);
        }
        // A corrected carrier also became the office's answer for that PMS
        // carrier — say so, since it affects the next patients too.
        const repointNote = describeRepoint(response.json && response.json.mapping_repointed);
        if (repointNote) message.info(repointNote, 6);

        getPatientPolicies();
        handleReadData2();
        // setShowPatientInsuranceFormModal(false);
        message.success("Insurance data set successfully");
      }
    } catch (err) {
      message.error("Something went wrong while submitting form");
      console.error(err);
    } finally {
      setIsSendingEligibilityData(false);
    }
  }

  async function getPatientPolicies() {
    try {
      setIsGettingPatientInsuranceData(true);

      const response = await rootController.getPatientPolicies(patientID);

      if (response.status === 200) {
        setPatientPolicies(response.json);

        if (initialMode === "add") {
          setShowForm(true);
          setSelectedPolicy(undefined);
          form.resetFields();
        } else if (response.json.length <= 1) {
          setShowForm(true);

          if (response.json.length === 1) {
            setSelectedPolicy(response.json[0]);
          } else {
            form.setFieldsValue({
              insurance_name: undefined,
              insurance_type: undefined,
              member_id: "",
              contract_number: "",
              relation_to_member: undefined,
              plan_name: undefined,
              fee_guide: undefined,
              specialist_fee_covered: undefined,
              deductible: undefined,
            });
          }
        }
      }
    } catch (err) {
      message.error("Something went wrong while getting patient policies.");
      console.error(err);
    } finally {
      setIsGettingPatientInsuranceData(false);
    }
  }

  function handleCloseModal() {
    const values = form.getFieldsValue();
    if ((!values.member_id || !values.insurance_type || !values.contract_number || !values.insurance_name)) {
      setCancelProcess(true);
    }
    setEditPatientInsuranceData(false);
    setShowPatientInsuranceFormModal(false);

    if (patientPolicies.length !== 1) {
      form.resetFields();
    }
  }

  function handleCancelButton() {
    form.resetFields();
    // Embedded has no policy-list sidebar to fall back to, so hiding the form
    // would leave an empty card with no way out — hand control back to the host
    // instead. (This matters even for a patient the host sees as policy-less:
    // `patientPolicies` here is unfiltered, so archived policies still count.)
    if (embedded || patientPolicies.length <= 1) {
      setShowPatientInsuranceFormModal(false);
    } else {
      setShowForm(false);
    }
  }

  function handleAddNewPolicy() {
    setShowForm(true);
    setSelectedPolicy(undefined);

    form.resetFields();
  }

  async function handleFetchFromSikka() {
    if (!patientID) {
      message.error("Patient context not available.");
      return;
    }
    try {
      setIsFetchingFromSikka(true);
      const response = await rootController.previewPatientPoliciesFromSikka(patientID);
      if (response.status < 250 && response.json && response.json.preview_token) {
        setSikkaPreview(response.json);
      } else if (response.json && response.json.error_type === "no_insurance_data") {
        message.info("PMS returned no insurance information for this patient.");
      } else {
        const err = (response.json && (response.json.error || response.json.detail)) || "Could not fetch from PMS";
        message.error(err);
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong while fetching from PMS.");
    } finally {
      setIsFetchingFromSikka(false);
    }
  }

  function handleSikkaSaved(savedPolicies) {
    setSikkaPreview(null);
    if (Array.isArray(savedPolicies) && savedPolicies.length > 0) {
      // Mirror the manual-create UX: keep the modal open, show the just-saved
      // policy in the form, and (if multiple) leave the side panel populated.
      setPatientPolicies(savedPolicies);
      const mostRecent = [...savedPolicies].sort(function (a, b) {
        const ta = a.last_synced_at ? new Date(a.last_synced_at).getTime() : 0;
        const tb = b.last_synced_at ? new Date(b.last_synced_at).getTime() : 0;
        return tb - ta;
      })[0];
      setShowForm(true);
      setSelectedPolicy(mostRecent);
    } else {
      // Fallback if the response shape is unexpected.
      getPatientPolicies();
    }
    if (handleReadData2) handleReadData2();
  }

  const patientInfo = displayPatient || patient || {};
  const lockedFields = (selectedPolicy && selectedPolicy.manually_edited_fields) || [];

  /**
   * Where Select/DatePicker popups render.
   *
   * antd portals them to document.body by default, which is fine inside a Modal
   * (every other caller) but leaves them unreachable when this form is embedded in
   * the Insurance Requests drawer — the dropdown simply never appears. Rendering
   * them next to their trigger keeps them inside the drawer's own DOM and stacking
   * context, so they open where you clicked.
   *
   * `undefined` for every non-embedded caller, so the modal keeps antd's default
   * behaviour exactly.
   */
  const popupContainer = embedded
    ? function (trigger) { return trigger.parentElement; }
    : undefined;

  /**
   * Picking a government plan (CDCP and friends) fills in everything that plan
   * dictates: it is always primary, the member is always the patient, and every
   * policy in the country shares one contract number.
   *
   * Overwrites rather than filling blanks — for these programs the canonical value
   * IS correct, so keeping a typed-in value would just preserve a mistake. Nothing
   * happens for commercial carriers: `governmentPlanValues` returns {}.
   *
   * This one handler covers every surface the form appears on — the treatment-plans
   * row, TreatmentCardStep2, and the Insurance Requests composer (same component,
   * `embedded`) — because they all render this Select.
   */
  function handleCarrierChange(carrierCode) {
    const values = governmentPlanValues(carrierCode, patientInfo);
    if (Object.keys(values).length) {
      form.setFieldsValue(values);
    }
  }

  /** The patient's own name and DOB, as the member fields want them. */
  function patientAsMember() {
    const dob = patientInfo.birth_date || patientInfo.date_of_birth;
    const parsed = dob ? dayjs(typeof dob === "string" ? dob.split("T")[0] : dob) : null;
    return {
      first_name: patientInfo.first_name || "",
      last_name: patientInfo.last_name || "",
      date_of_birth: parsed && parsed.isValid() ? parsed : undefined,
    };
  }

  /**
   * "Self" means the member IS the patient — so say so in the fields.
   *
   * Choosing Self is a statement of fact, not a preference, which is why it
   * overwrites rather than filling only blanks: a name already typed there is
   * either the same or wrong. Same reasoning as the government-plan fill, which
   * sets relation to self for exactly this reason.
   *
   * Switching AWAY from Self clears those fields, but only while they still match
   * the patient exactly. Values the user has since edited are theirs and are left
   * alone; what gets cleared is only ever the auto-fill, which a spouse or child
   * policy would otherwise silently carry as the wrong member.
   */
  function handleRelationChange(relation) {
    if (relation === "self") {
      form.setFieldsValue(patientAsMember());
      return;
    }
    const mine = patientAsMember();
    const current = form.getFieldsValue(["first_name", "last_name", "date_of_birth"]);
    const sameName =
      (current.first_name || "") === mine.first_name &&
      (current.last_name || "") === mine.last_name;
    const sameDob =
      (!current.date_of_birth && !mine.date_of_birth) ||
      (current.date_of_birth && mine.date_of_birth &&
        dayjs(current.date_of_birth).isSame(mine.date_of_birth, "day"));
    if (sameName && sameDob && (mine.first_name || mine.last_name)) {
      form.setFieldsValue({ first_name: "", last_name: "", date_of_birth: undefined });
    }
  }

  // The form itself, shared by both shells below.
  // The two field groups, built once so their ORDER can differ between the two
  // shells without either being duplicated.
  const memberInfoSection = (
    <>
      {sectionHeading("Member Info")}

      <div className="patient-policy-grid">
        <Form.Item
          className="patient-policy-field--full"
          name="relation_to_member"
          label={renderFieldLabel("Patient Relation", "relation_to_member", lockedFields, "Patient's relationship to the plan member / subscriber")}
        >
          <Select
            getPopupContainer={popupContainer}
            placeholder="Select patient relation"
            className="patient-policy-input"
            allowClear
            options={[
              { value: "self", label: "Self" },
              { value: "spouse", label: "Spouse" },
              { value: "child", label: "Child" },
              { value: "other", label: "Other" },
            ]}
            onChange={handleRelationChange}
          />
        </Form.Item>
      </div>

      <div className="patient-policy-grid patient-policy-grid--3">
        <Form.Item name="first_name" label={renderFieldLabel("First Name", "first_name", lockedFields)}>
          <Input placeholder="Enter member first name" className="patient-policy-input" />
        </Form.Item>

        <Form.Item name="last_name" label={renderFieldLabel("Last Name", "last_name", lockedFields)}>
          <Input placeholder="Enter member last name" className="patient-policy-input" />
        </Form.Item>

        <Form.Item name="date_of_birth" label={renderFieldLabel("DOB", "date_of_birth", lockedFields)}>
          <DatePicker
            getPopupContainer={popupContainer}
            suffixIcon={<img src={calendar} alt="" />}
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            className="patient-policy-input patient-policy-datepicker full-width"
            placeholder="Select member date of birth"
          />
        </Form.Item>
      </div>

      <MemberDobSuggestion
        patientId={patientID}
        policyId={selectedPolicy && selectedPolicy.id}
        first={watchedFirst}
        last={watchedLast}
        active={!watchedDob}
        onUse={function (dob) { form.setFieldsValue({ date_of_birth: dayjs(dob, "YYYY-MM-DD") }); }}
      />
    </>
  );

  const insurancePlanSection = (
    <>
      {sectionHeading("Insurance Plan")}

      <div className="patient-policy-grid">
        <Form.Item
          className="patient-policy-field--full"
          name="plan_name"
          label={renderFieldLabel("Plan Name", "plan_name", lockedFields)}
        >
          <Input placeholder="Enter plan name" className="patient-policy-input" />
        </Form.Item>

        <Form.Item
          name="insurance_name"
          label={renderFieldLabel("Carrier", "insurance_name", lockedFields)}
          rules={[{ required: true, message: "Please select a carrier" }]}
        >
          <Select
            getPopupContainer={popupContainer}
            placeholder="Select carrier"
            className="patient-policy-input"
            showSearch
            filterOption={function (input, option) {
              return (option.label || "").toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
            value={form.getFieldValue("insurance_name") || undefined}
            options={carrierOptions}
            loading={isLoadingCarriers}
            onChange={handleCarrierChange}
          />
        </Form.Item>

        <Form.Item
          name="insurance_type"
          label={renderFieldLabel("Ordinal", "insurance_type", lockedFields)}
          rules={[{ required: true, message: "Please select an ordinal" }]}
        >
          <Select
            getPopupContainer={popupContainer}
            placeholder="Select ordinal"
            className="patient-policy-input"
            options={[
              { value: "primary", label: "Primary" },
              { value: "secondary", label: "Secondary" },
              { value: "tertiary", label: "Tertiary" },
              { value: "other", label: "Other" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="contract_number"
          label={renderFieldLabel("Group/Contract #", "contract_number", lockedFields)}
          rules={[{ required: true, message: "Please enter the group/contract number" }]}
        >
          <Input placeholder="Enter group/contract number" className="patient-policy-input" />
        </Form.Item>

        <Form.Item
          name="member_id"
          label={renderFieldLabel("Member/Subscriber ID", "member_id", lockedFields)}
          rules={[{ required: true, message: "Please enter the member/subscriber ID" }]}
        >
          <Input placeholder="Enter member/subscriber ID" className="patient-policy-input" />
        </Form.Item>
      </div>
    </>
  );

  const formBody = (
    <Form form={form} name="eligibility" disabled={isSendingEligibilityData} layout="vertical" colon={false} requiredMark={false} style={{ paddingBottom: 8 }}>

    {!embedded && <PatientInfoCard patient={patientInfo} />}

    {/* Insurance Plan leads in the embedded (composer side-panel) form: picking
        the carrier there auto-fills the member fields for a government plan, so
        the field that drives the rest should come first. The modal keeps its
        long-standing order so nothing shifts for existing users. */}
    {embedded ? (
      <>
        {insurancePlanSection}
        {memberInfoSection}
      </>
    ) : (
      <>
        {memberInfoSection}
        {insurancePlanSection}
      </>
    )}

    <div className="patient-policy-form-footer">
      <Button onClick={() => handleCancelButton()}>Cancel</Button>

      <Form.Item label={null} style={{ marginBottom: 0 }}>
        <Button
          type="primary"
          htmlType="submit"
          onClick={handleUpdateOrCreatePolicy}
          loading={isSendingEligibilityData}
        >
          {selectedPolicy && selectedPolicy.id ? "Update Policy" : "Save Policy"}
        </Button>
      </Form.Item>
    </div>
  </Form>
  );

  // Embedded: no <Modal>, no Manage-Policies header, no policy-list sidebar and
  // no nested Sikka review (that belongs to "Fetch from PMS", which isn't rendered
  // here). Everything below the render — submit, create-vs-update, value
  // normalisation, the carrier list — is shared and untouched.
  if (embedded) {
    return (
      <div className="patient-policy-embedded">
        {isGettingPatientInsuranceData ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : showForm ? (
          formBody
        ) : null}
      </div>
    );
  }

  return (
    <Modal
      open={showPatientInsuranceFormModal}
      onCancel={handleCloseModal}
      footer={null}
      width={patientPolicies.length > 1 ? 1400 : 880}
      closeIcon={null}
      maskClosable={true}
      zIndex={1100}
    >
      <div className="patient-policy-modal-header">
        <div className="patient-policy-modal-header__title-wrap">
          <div className="patient-policy-modal-header__eyebrow">Insurance</div>
          <Typography.Title level={4} style={{ margin: 0, lineHeight: 1.2 }}>
            Manage Policies
          </Typography.Title>
          <div className="patient-policy-modal-header__subtitle">
            {patientPolicies.length === 0
              ? "No policies on file yet — add one or fetch from the PMS."
              : patientPolicies.length === 1
                ? "1 policy on file"
                : `${patientPolicies.length} policies on file`}
          </div>
        </div>

        <div className="patient-policy-modal-header__actions">
          <Button
            icon={<SyncOutlined />}
            loading={isFetchingFromSikka}
            onClick={handleFetchFromSikka}
            className="patient-policy-modal-header__pms-btn"
          >
            Fetch from PMS
          </Button>

          <Button type="primary" onClick={handleAddNewPolicy}>
            Add New
          </Button>

          <Button
            onClick={handleCloseModal}
            className="patient-policy-modal-header__close"
            aria-label="Close"
          >
            <img src={close} alt="" />
          </Button>
        </div>
      </div>

      {isGettingPatientInsuranceData ? (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 0" }}>
          <Spin />
        </div>
      ) : (
        <div className="patient-policy-modal-body custom-scroll">
          {patientPolicies.length > 1 && (
            <div className="patient-policy-list custom-scroll">
              <div className="patient-policy-list__heading">Policies</div>
              {patientPolicies.map((policy) => {
                const isSelected = selectedPolicy && policy.id === selectedPolicy.id;
                const ordinal = (policy.insurance_type_display || "").toLowerCase();
                return (
                  <button
                    type="button"
                    key={policy.id}
                    className={`patient-policy-card${isSelected ? " patient-policy-card--selected" : ""}`}
                    onClick={() => {
                      setShowForm(true);
                      setSelectedPolicy(policy);
                    }}
                  >
                    <span className="patient-policy-card__accent" aria-hidden />
                    <div className="patient-policy-card__header">
                      <div className="patient-policy-card__carrier" title={policy.insurance_name_display}>
                        {policy.insurance_name_display || "Unnamed carrier"}
                      </div>
                      {policy.insurance_type_display ? (
                        <span className={`patient-policy-card__ordinal patient-policy-card__ordinal--${ordinal}`}>
                          {policy.insurance_type_display}
                        </span>
                      ) : null}
                    </div>
                    <div className="patient-policy-card__ids">
                      <div>
                        <div className="patient-policy-meta-label">Member ID</div>
                        <div className="patient-policy-meta-value patient-policy-meta-value--mono">{policy.member_id || "—"}</div>
                      </div>
                      <div>
                        <div className="patient-policy-meta-label">Group / Contract</div>
                        <div className="patient-policy-meta-value patient-policy-meta-value--mono">{policy.contract_number || "—"}</div>
                      </div>
                    </div>
                    <div className="patient-policy-card__footer">
                      <span>{policy.plan_name || "No plan name"}</span>
                      <span className="patient-policy-card__separator" aria-hidden>·</span>
                      <span>{policy.relation_to_member_display || "Relation —"}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {showForm ? (
            <div style={{ flex: 1, minWidth: 0 }}>
              {formBody}
            </div>
          ) : (
            <div className="patient-policy-empty-state">
              <div className="patient-policy-empty-state__glyph" aria-hidden>
                <span>+</span>
              </div>
              <div className="patient-policy-empty-state__title">Choose a policy or add a new one</div>
              <div className="patient-policy-empty-state__hint">
                {patientPolicies.length > 1
                  ? "Pick one from the list on the left to edit its details."
                  : "Use “Add New” above to start a fresh policy, or pull from the PMS."}
              </div>
            </div>
          )}
        </div>
      )}

      <SikkaPolicyReviewModal
        open={!!sikkaPreview}
        preview={sikkaPreview}
        patientID={patientID}
        onClose={() => setSikkaPreview(null)}
        onSaved={handleSikkaSaved}
      />
    </Modal>
  );
}
